import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { authAPI } from '../services/api';
import { logout } from '../utils/auth';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading');
  const verifiedRef = useRef(false);

  useEffect(() => {
    // Clear any existing session so user must login fresh after verification
    logout();
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    if (verifiedRef.current) return;
    verifiedRef.current = true;

    const verify = async () => {
      try {
        await authAPI.verifyEmail(token);
        setStatus('success');
      } catch {
        setStatus('error');
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-3 text-center">
          <div className="flex justify-center mb-4">
            {status === 'loading' && (
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}
            {status === 'success' && (
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
            )}
            {status === 'error' && (
              <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl font-bold">
            {status === 'loading' && 'Verifying Email...'}
            {status === 'success' && 'Email Verified!'}
            {status === 'error' && 'Verification Failed'}
          </CardTitle>
          <CardDescription className="text-base">
            {status === 'loading' && 'Please wait while we verify your email address.'}
            {status === 'success' && 'Your email has been successfully verified. You can now log in to SportiZn.'}
            {status === 'error' && 'This verification link is invalid or has already been used.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {status === 'success' && (
            <Link to="/login">
              <Button className="w-full bg-primary text-white" data-testid="verify-success-login-btn">
                Log In Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          )}
          {status === 'error' && (
            <Link to="/login">
              <Button variant="outline" className="w-full" data-testid="verify-error-login-btn">
                Back to Login
              </Button>
            </Link>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
